import get from 'lodash/get';
import { format } from 'date-fns';
import { Data, EventData } from 'prismic/types';
import CmsElmtWrapper from 'components/organisms/CmsElmtWrapper/CmsElmtWrapper';
import PageBackground from 'components/atoms/PageBackground/PageBackground';
import Header from 'components/organisms/Header/Header';
import { Footer } from 'components/organisms/Footer';
import { Hero } from 'components/organisms/Hero';
import { Separator } from 'components/organisms/Separator';
import { MuseumPlan } from 'components/organisms/MuseumPlan';
import ComponentsRenderer from './partials/ComponentsRenderer';
import TemplateWrapper from './partials/TemplateWrapper';
import { HeaderSpacer } from 'components/atoms/HeaderSpacer';

interface EventTemplateProps {
  data: EventData;
  header: any;
  footer: Data;
}

const getLinkedLocationLabel = (location: any): string | null => {
  if (!location) return null;

  if (typeof location === 'string') return location;

  const houseNr = location?.data?.house_nr;
  const name = typeof location?.data?.name === 'string' ? location.data.name : null;
  if (name) {
    return typeof houseNr === 'number' ? `${houseNr} ${name}` : name;
  }

  const displayName = location?.data?.display_name?.[0]?.text;
  if (typeof displayName === 'string' && displayName) {
    return typeof houseNr === 'number' ? `${houseNr} ${displayName}` : displayName;
  }

  const title = location?.data?.title?.[0]?.text;
  if (typeof title === 'string' && title) {
    return typeof houseNr === 'number' ? `${houseNr} ${title}` : title;
  }

  return typeof houseNr === 'number' ? String(houseNr) : null;
};

const EventTemplate: React.FC<EventTemplateProps> = ({ data, header, footer }) => {
  const { alternate_languages } = data;

  const termineArray = data.data.body.map(
    (item: {
      items: {
        [x: string]: string | number | Date;
        event_start: string | number | Date;
        event_end: string | number | Date;
        default_location?: any;
      }[];
    }) => {
      const eventStart = new Date(item.items[0].event_start);
      const nextDateEnd = new Date(item.items[0].event_end);
      const loc = (item.items[0] as any).default_location;
      const houseNr = loc?.data?.house_nr ?? null;
      const locName = loc?.data?.name ?? loc?.data?.display_name?.[0]?.text ?? loc?.data?.title?.[0]?.text ?? null;
      const locOrt = loc?.data?.ort ?? null;
      return {
        nextDate: new Date(item.items[0].event_start),
        nextTimeslot: `${format(eventStart, 'kk:mm')} - ${format(nextDateEnd, 'kk:mm')}`,
        location: getLinkedLocationLabel(loc),
        house_nr: typeof houseNr === 'number' ? houseNr : null,
        locationName: typeof locName === 'string' ? locName : null,
        ort: typeof locOrt === 'string' ? locOrt : null,
      };
    }
  );

  const heroPrimary = {
    headline: data.data.title,
    image: data.data.image,
    intro_text: data.data.description,
    subheadline: '',
    show_breadcrumb: true,
    show_social_sharing: true,
    big_image: false,
    times: termineArray,
  };
  return (
    <TemplateWrapper pageId={data.id} data={data}>
      <PageBackground />
      <Header {...header} alternateLanguages={alternate_languages} />
      <HeaderSpacer />
      <CmsElmtWrapper sliceName="hero" nextSlice="default">
        <Hero primary={heroPrimary} />
      </CmsElmtWrapper>
      <ComponentsRenderer slices={get(data, 'data.body1', [])} />
      <Separator primary={{ horizontal_line: true }}></Separator>
      <MuseumPlan />
      <Footer data={get(footer, 'data.body', [])} />
    </TemplateWrapper>
  );
};

export default EventTemplate;
